import {
  API_COMPANY_LEAVE_POLICY,
  API_COMPANY_LEAVE_REQUEST_PAGINATED,
} from "@/constants/apiEndpoints";
import type {
  TApproveCompanyLeaveRequestDataRequest,
  TApproveCompanyLeaveRequestDataResponse,
  TCompanyLeaveStatisticsRequest,
  TCompanyLeaveStatisticsResponse,
  TLeaveRequestStatResponse,
  IModifyCompanyLeaveRequestDataRequest,
  IOnLeaveQueryParams,
  TOnLeaveResponse,
  TRejectCompanyLeaveRequestDataRequest,
  TRejectCompanyLeaveRequestDataResponse,
  TApproveAmendmentRequest,
  TApproveAmendmentResponse,
  TRejectAmendmentRequest,
  TRejectAmendmentResponse,
  TGetCompanyUserLeaveBalancesResponse,
} from "@/types/(company)/leaveRequest.interface";
import type {
  TGetAllCompanyLeavesDataRequest,
  LeaveResponseCompany,
  TLeaveResponseCompanyEmployeeById,
  TMsGetLeavePolicyResponse,
} from "@/types/(my-space)/leaves.interface";
import type { ILeaveTypesListingData } from "@/types/(company)/leaveTypes.interface";
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";

export const companyLeaveRequestApi = createApi({
  reducerPath: "companyLeaveRequestApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["LeaveRequestStat", "getOnLeave", "getCompanyStatistics", "getAllCompanyLeaves"],
  endpoints: (builder) => ({
    // FETCH: COMPANY SIDE ALL LEAVES
    getAllCompanyLeaves: builder.query<LeaveResponseCompany, TGetAllCompanyLeavesDataRequest>({
      query: (params) => {
        return {
          url: API_COMPANY_LEAVE_REQUEST_PAGINATED,
          method: "GET",
          params,
        };
      },
      providesTags: ["getAllCompanyLeaves"],
    }),
    getLeaveRequestStat: builder.query<TLeaveRequestStatResponse, void>({
      query: () => ({
        url: "/company/leave-requests/dashboard-stats",
        method: "GET",
      }),
      providesTags: ["LeaveRequestStat"],
    }),
    getOnLeave: builder.query<TOnLeaveResponse, IOnLeaveQueryParams>({
      query: (params) => ({
        url: "/company/leave-requests/on-leave",
        method: "GET",
        params,
      }),
      providesTags: ["getOnLeave"],
    }),
    getCompanyStatistics: builder.query<TCompanyLeaveStatisticsResponse, TCompanyLeaveStatisticsRequest>({
      query: () => {
        return {
          url: "/company/leave-requests/dashboard-stats",
          method: "GET",
        };
      },
      providesTags: ["getCompanyStatistics"],
    }),
    modifyCompanyLeave: builder.mutation<any, IModifyCompanyLeaveRequestDataRequest>({
      query: ({ id, ...body }) => {
        return {
          url: `/company/leave-requests/${id}/request-update`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["getCompanyStatistics", "getAllCompanyLeaves"],
    }),
    approveCompanyLeave: builder.mutation<
      TApproveCompanyLeaveRequestDataResponse,
      TApproveCompanyLeaveRequestDataRequest
    >({
      query: (params) => {
        return {
          url: `/company/leave-requests/${params.id}/approve`,
          method: "PUT",
          body: params,
        };
      },
      invalidatesTags: ["getCompanyStatistics", "getAllCompanyLeaves"],
    }),
    rejectCompanyLeave: builder.mutation<TRejectCompanyLeaveRequestDataResponse, TRejectCompanyLeaveRequestDataRequest>(
      {
        query: (params) => {
          return {
            url: `/company/leave-requests/${params.id}/reject`,
            method: "PUT",
            body: {
              reasonOfRejection: params.reasonOfRejection,
            },
          };
        },
        invalidatesTags: ["getCompanyStatistics", "getAllCompanyLeaves"],
      }
    ),
    approveAmendment: builder.mutation<TApproveAmendmentResponse, TApproveAmendmentRequest>({
      query: ({ id, amendmentId }) => {
        return {
          url: `/company/leave-requests/${id}/amendment/${amendmentId}/approve`,
          method: "PUT",
        };
      },
      invalidatesTags: ["getCompanyStatistics", "getAllCompanyLeaves"],
    }),
    rejectAmendment: builder.mutation<TRejectAmendmentResponse, TRejectAmendmentRequest>({
      query: ({ id, amendmentId, reasonOfRejection }) => {
        return {
          url: `/company/leave-requests/${id}/amendment/${amendmentId}/reject`,
          method: "PUT",
          body: { reasonOfRejection },
        };
      },
      invalidatesTags: ["getCompanyStatistics", "getAllCompanyLeaves"],
    }),
    createEmployeeLeaveRequest: builder.mutation<IApiResponse<void>, FormData>({
      query: (body) => {
        return {
          url: "/company/leave-requests",
          method: "POST",
          body,
        };
      },
    }),
    getCompanyLeaveRequestById: builder.query<TLeaveResponseCompanyEmployeeById, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/company/leave-requests/${id}`,
          method: "GET",
        };
      },
    }),
    getCompanyLeavePolicy: builder.query<
      TMsGetLeavePolicyResponse,
      { leaveTypeId: number; userId: number }
    >({
      query: ({ leaveTypeId, userId }) => ({
        url: API_COMPANY_LEAVE_POLICY,
        params: { leaveTypeId, userId },
      }),
    }),
    getCompanyUserLeaveBalances: builder.query<
      TGetCompanyUserLeaveBalancesResponse,
      { userId: number }
    >({
      query: ({ userId }) => ({
        url: `/company/leave-requests/users/${userId}/balances`,
        method: "GET",
      }),
    }),
    getApplicableLeaveTypesForUser: builder.query<
      { success: boolean; message: string; data: ILeaveTypesListingData[] },
      { userId: number }
    >({
      query: ({ userId }) => ({
        url: `/company/leave-requests/users/${userId}/applicable-leave-types`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAllCompanyLeavesQuery,
  useGetLeaveRequestStatQuery,
  useGetOnLeaveQuery,
  useGetCompanyStatisticsQuery,
  useModifyCompanyLeaveMutation,
  useApproveCompanyLeaveMutation,
  useRejectCompanyLeaveMutation,
  useApproveAmendmentMutation,
  useRejectAmendmentMutation,
  useGetCompanyLeaveRequestByIdQuery,
  useCreateEmployeeLeaveRequestMutation,
  useGetCompanyLeavePolicyQuery,
  useGetCompanyUserLeaveBalancesQuery,
  useGetApplicableLeaveTypesForUserQuery,
} = companyLeaveRequestApi;
